import React, { useState } from 'react';

import { Loading } from '../../components/Loading/Loading';
import { pushNotify } from '../../components/Notifications';
import { useGetReconciliationQuery, useLazyExportReconciliationQuery } from '../../redux/services/AnalystApi';
import { type AnalystFiltersParams } from '../../redux/types/AnalystTypes';
import { AnalystPage } from './components/AnalystPage';
import { PeriodFilters } from './components/PeriodFilters';
import { errorText, fmt, fmtInt, previousMonth } from './helpers';
import styles from './Analyst.module.css';

export const AnalystReconciliation = () => {
  const last = previousMonth();
  const [filters, setFilters] = useState<AnalystFiltersParams>({ date_from: last.from, date_to: last.to, merchant: '' });
  const ready = Boolean(filters.merchant && filters.date_from && filters.date_to);
  const params = { merchant: filters.merchant || '', date_from: filters.date_from || '', date_to: filters.date_to || '' };
  const { data, isFetching, isError, error } = useGetReconciliationQuery(params, { skip: !ready });
  const [exportFile, { isFetching: isExporting }] = useLazyExportReconciliationQuery();

  const download = async () => {
    const result = await exportFile(params);
    if (result.error || typeof result.data !== 'string') {
      pushNotify(errorText(result.error), 'Сверка');
      return;
    }
    const link = document.createElement('a');
    link.href = result.data;
    link.download = `Сверка ${data?.merchant.username || ''} ${params.date_from}–${params.date_to}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(result.data);
  };

  return (
    <AnalystPage
      title='Сверка с мерчантом'
      hint={
        <>
          Взаиморасчёты мерчанта и площадки за период в USDT, по шаблону ежемесячной сверки. Остаток: «+» — площадка
          должна мерчанту, «−» — мерчант должен площадке. В остаток идут оплаченные заявки, успешные выплаты, ручные
          корректировки и завершённые сеттлменты (по дате создания); сеттлменты в работе показаны отдельно.
        </>
      }
    >
      <PeriodFilters value={filters} onChange={setFilters} fields={['merchant']} merchantRequired />
      {!ready && <div className={styles.empty}>Выберите мерчанта и период</div>}
      {isError && <div className={styles.error}>{errorText(error)}</div>}
      {ready && !data && isFetching && <Loading />}
      {ready && data && (
        <div style={{ opacity: isFetching ? 0.55 : 1 }}>
          <div className={styles.filters}>
            <button type='button' className={`${styles.button} ${styles.primary}`} disabled={isExporting} onClick={download}>
              {isExporting ? 'Готовим файл…' : 'Скачать в Excel'}
            </button>
            <span className={styles.note}>
              Сейчас: площадка должна {fmt(data.current.available_to_merchant, 3)} · мерчант должен{' '}
              {fmt(data.current.available_to_platform, 3)} · сеттлменты в работе {fmt(data.current.settlements_pending_usdt, 3)} USDT
            </span>
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table} style={{ maxWidth: 1100 }}>
              <thead>
                <tr>
                  <th>Сверка {data.merchant.username}</th>
                  <th className={styles.num}>USDT</th>
                  <th>Пояснение</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.key} className={row.key === 'opening' || row.key === 'closing' ? styles.total : ''}>
                    <td>{row.label}</td>
                    <td className={styles.num}>{fmt(row.value_usdt, 3)}</td>
                    <td className={styles.note}>{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2>Pay-in по валютам и методам</h2>
          {data.breakdown.payin.length ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Валюта</th>
                    <th>Метод</th>
                    <th className={styles.num}>Заявок</th>
                    <th className={styles.num}>Сумма в валюте</th>
                    <th className={styles.num}>USDT до комиссии</th>
                    <th className={styles.num}>Комиссия, USDT</th>
                    <th className={styles.num}>Зачислено, USDT</th>
                  </tr>
                </thead>
                <tbody>
                  {data.breakdown.payin.map((r) => (
                    <tr key={`${r.currency}-${r.method}`}>
                      <td>{r.currency || '—'}</td>
                      <td>{r.method || '—'}</td>
                      <td className={styles.num}>{fmtInt(r.count)}</td>
                      <td className={styles.num}>{fmt(r.amount_fiat)}</td>
                      <td className={styles.num}>{fmt(r.gross_usdt, 3)}</td>
                      <td className={styles.num}>{fmt(r.fee_usdt, 3)}</td>
                      <td className={styles.num}>{fmt(r.credited_usdt, 3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.empty}>Оплаченных заявок за период нет</div>
          )}

          <h2>Pay-out по валютам и методам</h2>
          {data.breakdown.payout.length ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Валюта</th>
                    <th>Метод</th>
                    <th className={styles.num}>Выплат</th>
                    <th className={styles.num}>Сумма в валюте</th>
                    <th className={styles.num}>Долг мерчанта, USDT</th>
                  </tr>
                </thead>
                <tbody>
                  {data.breakdown.payout.map((r) => (
                    <tr key={`${r.currency}-${r.method}`}>
                      <td>{r.currency || '—'}</td>
                      <td>{r.method || '—'}</td>
                      <td className={styles.num}>{fmtInt(r.count)}</td>
                      <td className={styles.num}>{fmt(r.amount_fiat)}</td>
                      <td className={styles.num}>{fmt(r.debt_usdt, 3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.empty}>Успешных выплат за период нет</div>
          )}
        </div>
      )}
    </AnalystPage>
  );
};
